import { z } from 'zod';
import { AUDIT_REASON_MIN_LENGTH } from './audit-reasons.ts';
import { centavos, pageQuery } from './envelope.ts';
import { paymentMethodId } from './player-money.ts';
import { withdrawalStepUp } from './player-two-factor.ts';
import {
  nameMatch,
  parkedReason,
  payoutAccountStatus,
  payoutState,
  type NameMatch,
  type ParkedReason,
  type PayoutAccountStatus,
  type PayoutState,
} from './payout-closed-sets.ts';

/**
 * REGISTERED PAYOUT ACCOUNTS, AND WHAT BECAME OF A WITHDRAWAL ONCE IT WAS SENT.
 *
 * Its own file rather than more of `player-money.ts`, which describes the money a
 * player ASKS for. Everything here is about where money may be SENT and, once a
 * payout provider has been handed a withdrawal, where that stands. The API that
 * writes these rows and the console that reads them share this file, so neither
 * holds its own idea of what a state or a reason is called.
 *
 * ── THE WORDS ARE HERE ON PURPOSE ─────────────────────────────────────────────
 *
 * `PAYOUT_STATE_WORDS`, `PARKED_REASON_WORDS` and `PAYOUT_ACCOUNT_STATUS_WORDS`
 * are the one place a closed set becomes something a person reads, so no screen
 * ever prints `NOT_SENT` or `ATTEMPTS_EXHAUSTED` at somebody. Each is a Record
 * over its set: a value added to a set without a word fails to compile.
 *
 * ── BOTH APPS IMPORT THESE WORDS ──────────────────────────────────────────────
 *
 * The console and the player portal both depend on this package, and both apps
 * import these words from here rather than restating them: a word changed here is
 * changed on every screen that shows it.
 */

/* ── Closed sets ───────────────────────────────────────────────────────────── */

/* Declared in `payout-closed-sets.ts`, which imports nothing but zod, so that
   `lists.ts` can type the withdrawal queue with them without importing this file
   (see that file's note); exported from here under the same names as before. */
export { nameMatch, parkedReason, payoutAccountStatus, payoutState };
export type { NameMatch, ParkedReason, PayoutAccountStatus, PayoutState };

/* ── The words ─────────────────────────────────────────────────────────────── */

export const PAYOUT_STATE_WORDS: Record<PayoutState, string> = {
  NOT_SENT: 'Recorded, not sent',
  SENT: 'Sent to StarPay',
  RETRY: 'Waiting to be sent again',
  PROCESSING: 'With the bank',
  PAID: 'Paid',
  FAILED: 'Failed and refunded',
  REVERSED: 'Returned by the bank',
};

/**
 * The operator's words for why a payout is waiting. A clause each, with no full
 * stop, so a screen can finish the sentence however it needs to.
 */
export const PARKED_REASON_WORDS: Record<ParkedReason, string> = {
  UNVERIFIED_ANSWER: 'StarPay’s answer carried no signature this platform could verify',
  ANSWER_DISAGREES: 'StarPay’s answer described a different payout from this one',
  STATE_UNPROVEN: 'StarPay reported a state whose meaning its documentation does not define',
  UNKNOWN_VOCABULARY: 'StarPay answered with a code or state this platform does not know',
  /* What is left for a person: restarting payouts lifts the stop and leaves this row parked, so the
     words send them to StarPay and then the one closing action the funds screen offers for this
     reason. Never "confirm paid": the park is written on a send's answer, StarPay refused the
     hand-over before any order was made, so there is no payment to confirm (the console offers no
     Confirm paid here and the route refuses one). Never "re-send": the only re-send a person makes
     is the same-id one, offered for NO_ORDER_FOUND alone. */
  DRIVER_FAULT:
    'parked while payouts were stopped by a credential or configuration fault; once payouts are restarted, ask StarPay what it holds, then refund',
  NO_ORDER_FOUND: 'StarPay holds no order for the hand-over it was sent',
  /* Only when three hand-overs were COUNTED: a payout StarPay kept scheduling (its calendar codes
     give an attempt back) reaches the 24-hour rule with fewer, and is NO_WORD_24H. */
  ATTEMPTS_EXHAUSTED: 'three hand-overs were made and none produced a final answer',
  /* "No FINAL word": a payout StarPay answered only with its calendar (577, 406, 512) or an
     unproven code for a day is parked for this too, and StarPay did answer it. */
  NO_WORD_24H: 'sent, and no final word from StarPay for 24 hours',
  /* The three ways a second order arises, and the reason that outranks every other park: the bank's
     duplicate code; StarPay reporting an order under a hand-over the platform has since replaced;
     and StarPay naming an order for a payout the platform has already refunded. */
  DUPLICATE_AT_BANK:
    'a second order may exist for this payout — the bank reported a duplicate, StarPay reports an order under a hand-over the platform has since replaced, or one for a payout the platform already refunded — so the player may have been paid twice',
};

export const PAYOUT_ACCOUNT_STATUS_WORDS: Record<PayoutAccountStatus, string> = {
  ACTIVE: 'Ready to use',
  PENDING_REVIEW: 'Waiting for review',
  REFUSED: 'Not accepted',
  RETIRED: 'Retired',
};

/* ── What a player types ───────────────────────────────────────────────────── */

/**
 * One part of the holder's name, as the bank prints it.
 *
 * StarPay takes the name as three fields of at most 32 characters each, and the
 * columns that keep them are the same width, so the limit is refused here
 * rather than cut short later. Latin accents and Ñ are allowed: a Filipino name
 * has them.
 */
export const holderNamePart = z
  .string()
  .trim()
  .min(1, 'Enter the name as the bank prints it.')
  .max(32, 'StarPay accepts at most 32 characters for each part of the name.')
  .regex(
    /^[A-Za-zÀ-ÖØ-öø-ÿÑñ' .-]+$/,
    'Use letters, spaces, apostrophes, full stops and hyphens only.',
  );

/** The account or mobile number as a person types it: spaces, hyphens and + are allowed, and at least four digits must remain. */
export const payoutAccountNumber = z
  .string()
  .trim()
  .min(4, 'Enter the account or mobile number.')
  .max(34, 'That is longer than any account number this platform accepts.')
  .regex(/^[0-9 +-]+$/, 'Use digits only — spaces, + and - are allowed.')
  .refine((v) => v.replace(/\D/g, '').length >= 4, { message: 'Enter at least four digits.' });

/* ── The player's side ─────────────────────────────────────────────────────── */

/**
 * Registering an account, which is an act of the same weight as a withdrawal and
 * is held to the same proof: `stepUp` is required, and its own two fields are
 * optional for the reason `withdrawalStepUp` gives.
 *
 * `bankCode` is absent on an e-wallet rail, where the rail itself is the
 * destination.
 */
export const registerPayoutAccountRequest = z.object({
  methodId: paymentMethodId,
  bankCode: z.string().trim().min(1).max(32).optional(),
  accountNumber: payoutAccountNumber,
  holderFirstName: holderNamePart,
  holderMiddleName: holderNamePart.optional(),
  holderLastName: holderNamePart,
  stepUp: withdrawalStepUp,
});
export type RegisterPayoutAccountRequest = z.infer<typeof registerPayoutAccountRequest>;

/** Retiring an account takes the same proof as registering one. */
export const retirePayoutAccountRequest = z.object({ stepUp: withdrawalStepUp });
export type RetirePayoutAccountRequest = z.infer<typeof retirePayoutAccountRequest>;

/** One bank StarPay will pay to, as the platform last heard the list. */
export const payoutBankRow = z.object({
  code: z.string(),
  name: z.string(),
  sendable: z.boolean(),
  underMaintenance: z.boolean().nullable(),
  maintenanceNote: z.string().nullable(),
});
export type PayoutBankRow = z.infer<typeof payoutBankRow>;

/**
 * A registered account as its OWNER sees it. Only the mask: the number itself is
 * readable in exactly one place, the vault, and never reaches a browser.
 *
 * NO REVIEWER'S REASON. A refusal is told to the player in the mail template's
 * words, never the reviewer's (payout-review.ts): the reason is written for the
 * audit trail and the next operator, and it stays on the operator's review row
 * (`payoutAccountReviewRow`). The player reads the standing alone.
 */
export const payoutAccountRow = z.object({
  id: z.string(),
  ref: z.string(),
  methodId: z.string(),
  methodName: z.string(),
  provider: z.string(),
  bankCode: z.string().nullable(),
  bankName: z.string().nullable(),
  bankSendable: z.boolean().nullable(),
  bankMaintenanceNote: z.string().nullable(),
  mask: z.string(),
  holderName: z.string(),
  nameMatch,
  status: payoutAccountStatus,
  isDefault: z.boolean(),
  createdAt: z.iso.datetime(),
  verifiedAt: z.iso.datetime().nullable(),
  retiredAt: z.iso.datetime().nullable(),
  /** A withdrawal to this account is waiting for approval, or has been sent and has no final answer yet. */
  inFlight: z.boolean(),
  /**
   * Whether a withdrawal to the account can be requested now: its rail offered to the player's brand, not
   * disabled, taking withdrawals, and payable on this deployment — false, for one, for a StarPay account while
   * no payout provider is configured. When it is false the rail is left off the withdraw screen and a request
   * naming it is refused. The same rule decides all three (the API's withdrawalCanBeRequestedOn; the request
   * route checks its conditions), so the screen never promises a payment it would refuse.
   */
  railOffered: z.boolean(),
});
export type PayoutAccountRow = z.infer<typeof payoutAccountRow>;

/* ── The operator's side ───────────────────────────────────────────────────── */

/**
 * An operator's reason, for the audit trail.
 *
 * THE FLOOR IS THE DATABASE'S OWN, `AUDIT_REASON_MIN_LENGTH`, and the message
 * carries the number. A contract that floors a reason lower than the audit
 * CHECK lets a nine-character one through to a 409 that tells somebody a
 * character short that the platform forbids what they were doing (see
 * `payouts.ts`).
 */
const operatorReason = z
  .string()
  .trim()
  .min(
    AUDIT_REASON_MIN_LENGTH,
    `Give a reason of at least ${AUDIT_REASON_MIN_LENGTH} characters — it is the audit trail.`,
  )
  .max(500);

export const payoutAccountReviewQuery = pageQuery.extend({
  brandId: z.string().optional(),
  status: payoutAccountStatus.optional(),
});
export type PayoutAccountReviewQuery = z.infer<typeof payoutAccountReviewQuery>;

/**
 * WHO IS SENT THE PLAYER'S ID ON THE REVIEW LIST: a reader holding any of these
 * permission keys — one who decides accounts (funds.approve) or reviews
 * identities (kyc.review). transactions.view opens the list and reads the
 * account, its holder's name and the verdict it waits for, never the name on the
 * ID, its kind or its last four. The API decides by this list
 * (modules/players/payout-review.ts); the console's drawer names these keys to a
 * reader who holds neither, and the console's test holds the drawer to this list.
 */
export const PAYOUT_REVIEW_IDENTITY_KEYS = ['funds.approve', 'kyc.review'] as const;

/**
 * An account in the review queue. The identity fields are the player's verified
 * identity, which a reviewer compares the holder's name with; null when there is
 * none on record — and null for a reader who reviews nothing.
 */
export const payoutAccountReviewRow = z.object({
  id: z.string(),
  ref: z.string(),
  playerId: z.string(),
  playerRef: z.string(),
  playerName: z.string(),
  brandName: z.string(),
  methodName: z.string(),
  bankName: z.string().nullable(),
  mask: z.string(),
  holderName: z.string(),
  /**
   * Whether the identity fields were sent to this reader: only to one who decides
   * accounts (funds.approve) or reviews identities (kyc.review) —
   * PAYOUT_REVIEW_IDENTITY_KEYS. False, the three are null whatever is on record,
   * and a screen says the ID is withheld rather than that there is none.
   */
  identityShown: z.boolean(),
  identityFullName: z.string().nullable(),
  identityDocumentType: z.string().nullable(),
  identityDocumentNumberMask: z.string().nullable(),
  nameMatch,
  status: payoutAccountStatus,
  createdAt: z.iso.datetime(),
  verifiedAt: z.iso.datetime().nullable(),
  verifiedByLabel: z.string().nullable(),
  refusedReason: z.string().nullable(),
  /**
   * Whether a withdrawal to the account can be requested now, by the one rule the player's withdraw
   * screen lists rails by and the owner's row (`payoutAccountRow.railOffered`) reads: its rail
   * offered to the account's brand, not disabled and taking withdrawals, and payable on this
   * deployment — false, for one, for a StarPay account while no payout provider is configured.
   * Accepted when it is false, no withdrawal to the account can be requested until its payment
   * method is available, and the review screen and the e-mail say so.
   */
  railOffered: z.boolean(),
});
export type PayoutAccountReviewRow = z.infer<typeof payoutAccountReviewRow>;

/** A person's verdict on an account waiting for review. Either verdict carries a reason. */
export const reviewPayoutAccountRequest = z.object({
  verdict: z.enum(['ACTIVE', 'REFUSED']),
  reason: operatorReason,
});
export type ReviewPayoutAccountRequest = z.infer<typeof reviewPayoutAccountRequest>;

/**
 * Which path brought an answer: the send's own reply, a query (the console's,
 * the reconcile job's, or the one a notification sets off), or a notification.
 */
export const payoutAnswerSource = z.enum(['SEND', 'QUERY', 'NOTIFICATION']);
export type PayoutAnswerSource = z.infer<typeof payoutAnswerSource>;

/**
 * THE LATEST ANSWER ABOUT ONE HAND-OVER, and the path it came by. A send's reply
 * is a hand-over's first answer; a query or a notification StarPay is believed
 * about — one about the order, code "200" or its "no order" — is recorded on the
 * hand-over it was about and becomes its latest. A send's reply that arrives
 * after such an answer (the release calls StarPay outside any lock) stays the
 * first: it never replaces the later word. Only what was said since the
 * hand-over was made, or re-sent under its own id, is its latest: an answer
 * from before a same-id re-send describes the hand-over before it. `code` and
 * `state` are StarPay's own, as received; `words` says what they mean.
 */
export const payoutLatestAnswer = z.object({
  at: z.iso.datetime(),
  source: payoutAnswerSource,
  code: z.string().nullable(),
  state: z.string().nullable(),
  words: z.string(),
});
export type PayoutLatestAnswer = z.infer<typeof payoutLatestAnswer>;

/**
 * One hand-over of a withdrawal to the provider, and what came back.
 * `answerCode`, `answerState` and `answeredAt` are the hand-over's own answer —
 * its send's reply, kept even when it came late. `latestAnswer` is the latest
 * word about it from any path (see `payoutLatestAnswer`), and null for a
 * hand-over nothing has answered since it was made.
 */
export const payoutAttemptRow = z.object({
  attemptNo: z.number().int(),
  msgId: z.string(),
  sentAt: z.iso.datetime(),
  answerCode: z.string().nullable(),
  answerState: z.string().nullable(),
  answeredAt: z.iso.datetime().nullable(),
  provedNoOrderAt: z.iso.datetime().nullable(),
  resentSameIdAt: z.iso.datetime().nullable(),
  latestAnswer: payoutLatestAnswer.nullable(),
});
export type PayoutAttemptRow = z.infer<typeof payoutAttemptRow>;

/**
 * Where a withdrawal's payout stands, with the provider's own words kept beside
 * the platform's reading of them (`channelFeeRaw` beside `channelFee`).
 *
 * Money is centavos in a string, as everywhere. `retryAfter` and `nextCheckAt`
 * are instants like the other `...At` fields.
 */
export const withdrawalPayout = z.object({
  state: payoutState,
  provider: z.string(),
  sendable: z.boolean(),
  msgId: z.string().nullable(),
  providerRef: z.string().nullable(),
  instapayRef: z.string().nullable(),
  /**
   * WHETHER THE PLATFORM'S ONE ASK FOR THE INSTAPAY REFERENCE IS SPENT. A send's answer cannot
   * carry the reference, so the platform asks StarPay for it afterwards: once, and only for a paid
   * payout. An answer about the order whose signature verifies (code "200", or StarPay's "no
   * order") spends the ask, with the reference or without it. An ask that gets no reply, or only a
   * refusal about the call, is given back and made again, until three in a row have gone that way;
   * a parked payout's is never given back, and an answer that does not verify parks the payout.
   * With no payout provider configured nothing is asked, so the ask stays to come. True means the
   * platform has no ask left to make for this payout; false means the ask is still to come. A
   * screen says a paid payout's reference is on its way only while this is false. It is read for a
   * paid payout only.
   */
  instapayRefAskSpent: z.boolean(),
  providerState: z.string().nullable(),
  providerCode: z.string().nullable(),
  providerMessage: z.string().nullable(),
  providerErrorCode: z.string().nullable(),
  thirdMsg: z.string().nullable(),
  quotedFee: centavos,
  channelFee: centavos.nullable(),
  channelFeeRaw: z.string().nullable(),
  sendAttempts: z.number().int(),
  retryAfter: z.iso.datetime().nullable(),
  nextCheckAt: z.iso.datetime().nullable(),
  queryFailures: z.number().int(),
  lastQueriedAt: z.iso.datetime().nullable(),
  parkedAt: z.iso.datetime().nullable(),
  parkedReason: parkedReason.nullable(),
  /**
   * The park's own sentence — what the answer that parked the row actually
   * said, which the reason's standing words cannot (ANSWER_DISAGREES covers a
   * "no order" about a payout StarPay had said was with the bank, and an order
   * number another payout holds). It is kept on the trail, not on the row: the
   * newest park recorded for this withdrawal. Null when the row is not parked,
   * or when no park of it was ever recorded.
   */
  parkedDetail: z.string().nullable(),
  sentAt: z.iso.datetime().nullable(),
  paidAt: z.iso.datetime().nullable(),
  reversedAt: z.iso.datetime().nullable(),
  accountMask: z.string().nullable(),
  bankName: z.string().nullable(),
  holderName: z.string().nullable(),
  /**
   * The admin reading this released the payout. Confirming it paid, refunding it
   * and closing its hold are a second person's (the route refuses the releaser
   * with MAKER_IS_CHECKER), so a screen can say so before anyone presses.
   */
  releasedByMe: z.boolean(),
  attempts: z.array(payoutAttemptRow),
});
export type WithdrawalPayout = z.infer<typeof withdrawalPayout>;

/**
 * What an operator does with a withdrawal that is waiting for a person. QUERY
 * asks StarPay again and needs no reason; CONFIRM_PAID, REFUND,
 * RESEND_SAME_ID and DISMISS_HOLD are the operator's own decision, and each
 * carries one.
 *
 * DISMISS_HOLD closes the hold on a payout whose money is already settled —
 * paid, or refunded — and moves no money: the exit for a row that waits on a
 * question nothing else will answer (StarPay's word that contradicted the
 * settlement, a duplicate a person has since checked). A payout still in flight
 * is asked about or confirmed, never dismissed. Like CONFIRM_PAID and REFUND it
 * is a second person's: the admin who released the payout may not close its
 * hold, since a hold exists so that somebody else looks at a contradiction.
 */
export const unparkWithdrawalRequest = z.discriminatedUnion('action', [
  z.object({ action: z.literal('QUERY') }),
  z.object({ action: z.literal('CONFIRM_PAID'), reason: operatorReason }),
  z.object({ action: z.literal('REFUND'), reason: operatorReason }),
  z.object({ action: z.literal('RESEND_SAME_ID'), reason: operatorReason }),
  z.object({ action: z.literal('DISMISS_HOLD'), reason: operatorReason }),
]);
export type UnparkWithdrawalRequest = z.infer<typeof unparkWithdrawalRequest>;

/** What the person who pressed Release is told happened, in a sentence and in the fields behind it. */
export const releaseOutcome = z.object({
  outcome: z.enum([
    'NOT_SENDABLE',
    'SENT_WAITING',
    'PAID',
    'RETRY',
    'FAILED_REFUNDED',
    'PARKED',
    'NO_ANSWER',
  ]),
  payoutState,
  sentence: z.string(),
  retryAfter: z.iso.datetime().nullable(),
  nextCheckAt: z.iso.datetime().nullable(),
  parkedReason: parkedReason.nullable(),
  cause: z.string().nullable(),
});
export type ReleaseOutcome = z.infer<typeof releaseOutcome>;

/**
 * The payout provider's status: which driver is on, whether the safety stop has
 * tripped, the provider's float, whether it can be reached, how many payouts are
 * in flight or waiting for a person, and the three behaviour flags.
 *
 * `inboxParked` counts the StarPay notifications still waiting for a person: one
 * the webhook filed PARKED while the payout it names was left waiting for an
 * operator counts while that payout waits (it is settled with the payout, never
 * on its own); any other — one that names no payout, one about a payout left as
 * it was — counts until a person marks it seen (`markNotificationSeenRequest`).
 * Brand-aware both ways, by the rule the list is read by: one that waits with a
 * payout counts within the reader's brand scope, as the payout does; one that
 * waits on its own belongs to no brand, and counts only for a reader with no
 * brand restriction. So the count is always the number of waiting rows the same
 * reader's list shows.
 *
 * `health` is the provider's connection check: `checkedAt` is when the check
 * this reading came from was made — the real driver keeps a reading for a
 * minute, as it keeps the float, so a screen every operator opens does not call
 * StarPay on every load. `reachable` is null, with no `checkedAt`, when nothing
 * was checked (the OFF driver has no provider to ask), and `words` says what the
 * reading is. Under the MOCK driver the reading is the mock's own, and the words
 * say StarPay itself was not asked.
 *
 * `driver` and `driverName` carry the same name: `driverName` is the name the
 * screens built after Task 22 read.
 *
 * `float` is null when the driver is OFF, so there is nothing to ask.
 * `walletBalance` and `queryTime` are the provider's own strings, kept as
 * received.
 */
export const payoutProviderStatus = z.object({
  driver: z.enum(['OFF', 'MOCK', 'STARPAY']),
  driverName: z.enum(['OFF', 'MOCK', 'STARPAY']),
  breaker: z.object({
    tripped: z.boolean(),
    reason: z.string().nullable(),
    trippedAt: z.iso.datetime().nullable(),
  }),
  float: z
    .object({
      ok: z.boolean(),
      walletBalance: z.string().nullable(),
      currency: z.string().nullable(),
      centavos: centavos.nullable(),
      payCenterName: z.string().nullable(),
      queryTime: z.string().nullable(),
    })
    .nullable(),
  health: z.object({
    reachable: z.boolean().nullable(),
    checkedAt: z.iso.datetime().nullable(),
    words: z.string(),
  }),
  inFlight: z.number().int(),
  parked: z.number().int(),
  inboxParked: z.number().int(),
  flags: z.object({
    resendSameMsgId: z.boolean(),
    autoRefundOnReversed: z.boolean(),
    playerBorneFee: z.boolean(),
  }),
});
export type PayoutProviderStatus = z.infer<typeof payoutProviderStatus>;

/** Resetting the payout safety stop, which puts payouts back on. An audited act, so it must say why. */
export const resetPayoutBreakerRequest = z.object({ reason: operatorReason });
export type ResetPayoutBreakerRequest = z.infer<typeof resetPayoutBreakerRequest>;

/* ── StarPay notifications waiting for a person ────────────────────────────── */

/**
 * The list, newest first, paged — and narrowed as every Player funds read is: `brandId` is the brand
 * the reader chose on the screen, one of theirs (another answers 403). A notification about a payout
 * is listed within that choice; one that names no payout belongs to no brand, and is listed for a
 * reader with no brand restriction whatever brand they chose. The Payout provider tile is read with
 * the same choice, so its count is the number of waiting rows this list shows.
 */
export const parkedNotificationsQuery = pageQuery.extend({
  brandId: z.string().min(1).max(64).optional(),
});
export type ParkedNotificationsQuery = z.infer<typeof parkedNotificationsQuery>;

/**
 * A StarPay notification the webhook filed for a person, newest first.
 *
 * `note` is the platform's own sentence about it, in the operator's words, and
 * nothing the provider or a failure wrote: the inbox's error text can hold an
 * exception's own message, and no screen reads it. `withdrawalRef` names the
 * payout the notification waits with — one it was matched to by that payout's
 * own hand-over, and that it left waiting for an operator — or is null. A
 * notification about a payout it left as it was (settled, or in flight with no
 * hold on it) waits on its own, so its `withdrawalRef` is null and its note
 * names the payout; so does a reference the notification merely quoted.
 *
 * `waiting` is whether it still counts as waiting for a person: one with a
 * `withdrawalRef` while that payout is held (it clears with the payout's hold,
 * never by hand); any other until a person marks it seen. `seenAt` and `seenBy`
 * (the admin's display name) are set only on one that waits on its own.
 */
export const parkedNotificationRow = z.object({
  id: z.string(),
  receivedAt: z.iso.datetime(),
  note: z.string(),
  withdrawalRef: z.string().nullable(),
  seenAt: z.iso.datetime().nullable(),
  seenBy: z.string().nullable(),
  waiting: z.boolean(),
});
export type ParkedNotificationRow = z.infer<typeof parkedNotificationRow>;

/** Marking a notification that waits on its own as seen: it leaves the count. A person's act, so it says why. */
export const markNotificationSeenRequest = z.object({ reason: operatorReason });
export type MarkNotificationSeenRequest = z.infer<typeof markNotificationSeenRequest>;
